import React from "react";
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Checkbox,
    Divider,
    IconButton,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import ManageHistoryIcon from "@mui/icons-material/ManageHistory";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {useTranslation} from "react-i18next";
import type {WorkEvidenceRecord} from "../WorkEvidenceTableComponent";
import {computeDaysWorked, getEffectiveValue, validateRecord} from "../WorkEvidenceTableComponent";
import StatusChip from "../StatusChip";
import {getAvatarColors, getInitials} from "../../../utils/avatarUtils";

// ─── Constants ────────────────────────────────────────────────────────────────

const HOUR_FIELDS: { key: keyof WorkEvidenceRecord; labelKey: string }[] = [
    {key: "regularHours", labelKey: "workEvidence.table.header.regularHours"},
    {key: "overTimeHours", labelKey: "workEvidence.table.header.overtimeHours"},
    {key: "nightHours", labelKey: "workEvidence.table.header.nightHours"},
    {key: "weekendHours", labelKey: "workEvidence.table.header.weekendHours"},
    {key: "holidayHours", labelKey: "workEvidence.table.header.holidayHours"},
];

const COMMENT_MAX_LENGTH = 500;

// ─── Props ────────────────────────────────────────────────────────────────────

interface WorkEvidenceTableComponentMobileProps {
    sortableData: WorkEvidenceRecord[];
    currentPage: number;
    itemsPerPage: number;
    isPeriodOpen: boolean;
    editedCells: { [key: string]: string };
    activeCellId: string | null;
    handleCellActivate: (cellId: string) => void;
    handleCellBlur: () => void;
    handleCellValueChange: (cellId: string, value: string) => void;
    selected: string[];
    isSelected: (id: string) => boolean;
    onRowCheckboxClick: (event: React.MouseEvent, id: string) => void;
    commentDrafts: { [employeeId: string]: string };
    onCommentChange: (employeeId: string, value: string) => void;
    isLoading?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

const WorkEvidenceTableComponentMobile: React.FC<WorkEvidenceTableComponentMobileProps> = ({
                                                                                               sortableData,
                                                                                               currentPage,
                                                                                               itemsPerPage,
                                                                                               isPeriodOpen,
                                                                                               editedCells,
                                                                                               activeCellId,
                                                                                               handleCellActivate,
                                                                                               handleCellBlur,
                                                                                               handleCellValueChange,
                                                                                               selected,
                                                                                               isSelected,
                                                                                               onRowCheckboxClick,
                                                                                               commentDrafts,
                                                                                               onCommentChange,
                                                                                               isLoading = false,
                                                                                           }) => {
    const {t} = useTranslation();
    const [expandedId, setExpandedId] = React.useState<string | null>(null);

    const paginatedData = React.useMemo(
        () => sortableData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
        [sortableData, currentPage, itemsPerPage],
    );

    // Keep the expanded card within the current page's records.
    React.useEffect(() => {
        if (expandedId && !paginatedData.some((r) => r.employeeId === expandedId)) {
            setExpandedId(null);
        }
    }, [paginatedData, expandedId]);

    if (isLoading) {
        return (
            <Stack spacing={2}>
                {Array.from({length: 4}).map((_, i) => (
                    <Card key={`skeleton-${i}`} variant="outlined" sx={{backgroundColor: "#fff"}}>
                        <CardContent>
                            <Skeleton variant="text" width="50%" height={28} sx={{mb: 1}}/>
                            <Skeleton variant="rounded" width={80} height={22} sx={{mb: 1.5}}/>
                            {Array.from({length: 5}).map((__, j) => (
                                <Box key={j} sx={{display: "flex", justifyContent: "space-between", mb: 0.75}}>
                                    <Skeleton variant="text" width="40%"/>
                                    <Skeleton variant="text" width="25%"/>
                                </Box>
                            ))}
                        </CardContent>
                    </Card>
                ))}
            </Stack>
        );
    }

    if (paginatedData.length === 0) {
        return (
            <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", py: 6, gap: 1}}>
                <ManageHistoryIcon sx={{fontSize: 48, color: "text.disabled"}}/>
                <Typography variant="body1" color="text.secondary">{t('workEvidence.empty.title')}</Typography>
                <Typography variant="body2" color="text.disabled" sx={{textAlign: "center"}}>
                    {t('workEvidence.empty.description')}
                </Typography>
            </Box>
        );
    }

    const goToOffset = (offset: number) => {
        const currentIndex = paginatedData.findIndex((r) => r.employeeId === expandedId);
        if (currentIndex === -1) return;
        const nextIndex = currentIndex + offset;
        if (nextIndex < 0 || nextIndex >= paginatedData.length) return;
        setExpandedId(paginatedData[nextIndex].employeeId);
    };

    return (
        <Stack spacing={2}>
            {paginatedData.map((record) => {
                const rowSelected = isSelected(record.employeeId);
                const isExpanded = expandedId === record.employeeId;
                const validation = validateRecord(record, editedCells);
                const commentValue = commentDrafts[record.employeeId] ?? record.comment ?? "";
                const cardIndex = paginatedData.findIndex((r) => r.employeeId === record.employeeId);
                const avatarColors = getAvatarColors(`${record.firstName}${record.lastName}${record.employeeId}`);

                return (
                    <Card
                        key={record.employeeId}
                        variant="outlined"
                        sx={{
                            backgroundColor: rowSelected ? "action.selected" : "#fff",
                            transition: "box-shadow 0.2s ease",
                            "&:active": {transform: "scale(0.99)"},
                            "&:hover": {boxShadow: 2},
                        }}
                    >
                        <CardContent
                            sx={{cursor: "pointer", pb: isExpanded ? undefined : "12px !important"}}
                            onClick={() => setExpandedId(isExpanded ? null : record.employeeId)}
                        >
                            {/* Header: checkbox + name + status + expand/nav controls */}
                            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1}}>
                                <Box sx={{display: "flex", alignItems: "center", gap: 1, minWidth: 0}}>
                                    <Checkbox
                                        color="primary"
                                        checked={rowSelected}
                                        onClick={(e) => onRowCheckboxClick(e, record.employeeId)}
                                        aria-label={t('workEvidence.aria.selectRecord', {firstName: record.firstName, lastName: record.lastName})}
                                        sx={{p: 0.5}}
                                    />
                                    <Avatar sx={{
                                        width: 32,
                                        height: 32,
                                        bgcolor: avatarColors.bg,
                                        color: avatarColors.color,
                                        fontSize: "0.8rem",
                                        fontWeight: 600,
                                        flexShrink: 0,
                                    }}>
                                        {getInitials(record.firstName, record.lastName)}
                                    </Avatar>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                        sx={{wordBreak: "break-word", textAlign: "left"}}
                                    >
                                        {record.firstName} {record.lastName}
                                    </Typography>
                                </Box>

                                {isExpanded ? (
                                    <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}
                                         onClick={(e) => e.stopPropagation()}>
                                        <IconButton size="small" disabled={cardIndex <= 0}
                                                    onClick={() => goToOffset(-1)}>
                                            <ChevronLeftIcon fontSize="small"/>
                                        </IconButton>
                                        <IconButton size="small" disabled={cardIndex >= paginatedData.length - 1}
                                                    onClick={() => goToOffset(1)}>
                                            <ChevronRightIcon fontSize="small"/>
                                        </IconButton>
                                    </Box>
                                ) : (
                                    <ExpandMoreIcon sx={{color: "text.disabled", flexShrink: 0}}/>
                                )}
                            </Box>

                            {!isExpanded && (
                                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1}}>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('workEvidence.mobile.summary', {
                                            regularHours: record.regularHours ?? 0,
                                            overtime: record.overTimeHours ?? 0,
                                        })}
                                    </Typography>
                                    <StatusChip status={record.status}/>
                                </Box>
                            )}

                            {isExpanded && (
                                <Box onClick={(e) => e.stopPropagation()} sx={{mt: 1.5}}>
                                    <Box sx={{mb: 1.5}}>
                                        <StatusChip status={record.status}/>
                                    </Box>

                                    <Divider sx={{mb: 1.5}}/>

                                    {/* Editable hour fields */}
                                    <Box sx={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: 1.5,
                                        mb: 1.5,
                                    }}>
                                        {HOUR_FIELDS.map(({key, labelKey}) => {
                                            const cellId = `${record.employeeId}-${key}`;
                                            const isActive = activeCellId === cellId;
                                            const editedValue = editedCells[cellId];
                                            const currentValue = record[key] as number | null;

                                            return (
                                                <Box key={key}>
                                                    <Typography variant="caption" color="text.secondary"
                                                                sx={{display: "block", mb: 0.5}}>
                                                        {t(labelKey)}
                                                    </Typography>
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        value={isActive ? (editedValue ?? currentValue ?? "") : (editedValue ?? currentValue ?? "-")}
                                                        variant="outlined"
                                                        type={isPeriodOpen ? "number" : "text"}
                                                        disabled={!isPeriodOpen}
                                                        inputProps={{step: "0.01", inputMode: "decimal"}}
                                                        onFocus={() => isPeriodOpen && handleCellActivate(cellId)}
                                                        onChange={(e) => handleCellValueChange(cellId, e.target.value)}
                                                        onBlur={handleCellBlur}
                                                    />
                                                </Box>
                                            );
                                        })}
                                        <Box>
                                            <Typography variant="caption" color="text.secondary"
                                                        sx={{display: "block", mb: 0.5}}>
                                                {t('workEvidence.table.header.daysWorked')}
                                            </Typography>
                                            <Typography variant="body2" sx={{py: 1}} color="text.secondary">
                                                {computeDaysWorked(getEffectiveValue(record, editedCells, "regularHours"))}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Note */}
                                    <Typography variant="caption" color="text.secondary"
                                                sx={{display: "block", mb: 0.5}}>
                                        {t('workEvidence.note.title', {
                                            firstName: record.firstName,
                                            lastName: record.lastName,
                                        })}
                                    </Typography>
                                    <TextField
                                        multiline
                                        minRows={2}
                                        fullWidth
                                        size="small"
                                        value={commentValue}
                                        disabled={!isPeriodOpen}
                                        onChange={(e) => onCommentChange(record.employeeId, e.target.value.slice(0, COMMENT_MAX_LENGTH))}
                                    />
                                    <Typography variant="caption" color="text.disabled"
                                                sx={{display: "block", textAlign: "right", mt: 0.25}}>
                                        {commentValue.length} / {COMMENT_MAX_LENGTH}
                                    </Typography>

                                    <Divider sx={{my: 1.5}}/>

                                    {/* Validation checklist */}
                                    <Stack spacing={0.75}>
                                        <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                                            {validation.hoursFilled ? (
                                                <CheckCircleIcon sx={{fontSize: 16, color: "success.main"}}/>
                                            ) : (
                                                <CancelIcon sx={{fontSize: 16, color: "text.disabled"}}/>
                                            )}
                                            <Typography variant="caption">{t('workEvidence.validation.hoursFilled')}</Typography>
                                        </Box>
                                        <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                                            {validation.noNegativeValues ? (
                                                <CheckCircleIcon sx={{fontSize: 16, color: "success.main"}}/>
                                            ) : (
                                                <CancelIcon sx={{fontSize: 16, color: "error.main"}}/>
                                            )}
                                            <Typography variant="caption">{t('workEvidence.validation.noNegativeValues')}</Typography>
                                        </Box>
                                    </Stack>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                );
            })}
        </Stack>
    );
};

export default WorkEvidenceTableComponentMobile;
